
import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/auth';
import { Poll } from '@/types/poll';
import { VotingService, VoteSubmissionResult } from '@/services/voting/VotingService';
import { toast } from 'sonner';
import { debugVoteFlow } from '@/utils/debug';
import { generateAnonymousId } from '@/utils/anonymousId';

export interface CentralizedVotingHook {
  submitVote: (poll: Poll, optionId: number) => Promise<VoteSubmissionResult>;
  validateVote: (pollId: string) => Promise<boolean>;
  getUserVote: (pollId: string) => Promise<number | null>;
  getUserSelectedOption: (pollId: string) => Promise<number | null>;
  isProcessing: boolean;
}

export const useCentralizedVoting = (providedAnonymousId?: string): CentralizedVotingHook => {
  const { user, profile } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const activeSubmissions = useRef<Set<string>>(new Set());
  const [anonymousId, setAnonymousId] = useState<string | null>(providedAnonymousId || null);

  // Initialize anonymous ID if not provided and user is not authenticated
  const getAnonymousId = useCallback(async (): Promise<string | null> => {
    if (user) return null; // Don't use anonymous ID for authenticated users
    
    if (anonymousId) return anonymousId;
    if (providedAnonymousId) return providedAnonymousId;
    
    try {
      const id = await generateAnonymousId();
      setAnonymousId(id);
      return id;
    } catch (error) {
      debugVoteFlow('CENTRALIZED_VOTING_ANONYMOUS_ID_ERROR', { error });
      return null;
    }
  }, [user, anonymousId, providedAnonymousId]);

  const submitVote = useCallback(async (poll: Poll, optionId: number): Promise<VoteSubmissionResult> => {
    const submissionKey = `${poll.id}-${optionId}`;
    
    // Get the appropriate ID for this voting session
    const currentAnonymousId = await getAnonymousId();
    
    debugVoteFlow('CENTRALIZED_SUBMIT_VOTE', { 
      pollId: poll.id, 
      optionId, 
      userId: user?.id,
      anonymousId: currentAnonymousId ? 'present' : 'none',
      isProcessing,
      hasActiveSubmission: activeSubmissions.current.has(submissionKey),
      anonymousAllowed: poll.anonymous_allowed,
      publicVotingAllowed: poll.public_voting_allowed,
      userRole: profile?.role
    });

    // Prevent duplicate submissions for the same poll-option combination
    if (activeSubmissions.current.has(submissionKey)) {
      debugVoteFlow('CENTRALIZED_DUPLICATE_SUBMISSION_PREVENTED', { submissionKey });
      return { success: false, error: 'Vote submission already in progress for this option' };
    }

    if (isProcessing) {
      debugVoteFlow('CENTRALIZED_ALREADY_PROCESSING', {});
      return { success: false, error: 'Vote submission already in progress' };
    }

    // Enhanced voting eligibility check
    if (!user && (!poll.anonymous_allowed || !currentAnonymousId)) {
      debugVoteFlow('CENTRALIZED_CANNOT_VOTE_ANONYMOUS', { 
        isAuthenticated: !!user,
        anonymousAllowed: poll.anonymous_allowed,
        hasAnonymousId: !!currentAnonymousId
      });
      return { 
        success: false, 
        error: poll.anonymous_allowed 
          ? 'Anonymous voting setup failed. Please try again.' 
          : 'Please sign in to vote on this poll.' 
      };
    }

    // Check public voting permissions for authenticated public users
    if (user && profile?.role === 'public' && !poll.public_voting_allowed) {
      debugVoteFlow('CENTRALIZED_PUBLIC_VOTING_NOT_ALLOWED', { 
        userRole: profile.role,
        publicVotingAllowed: poll.public_voting_allowed
      });
      return {
        success: false,
        error: 'Public voting is not enabled for this poll.'
      };
    }

    setIsProcessing(true);
    activeSubmissions.current.add(submissionKey);

    try {
      const result = await VotingService.submitVote(
        poll.id,
        optionId,
        user?.id,
        currentAnonymousId || undefined,
        poll
      );

      debugVoteFlow('CENTRALIZED_VOTE_RESULT', { success: result.success, error: result.error });

      if (result.success) {
        // Enhanced success toast with points info
        if (result.pointsAwarded && result.pointsAwarded > 0) {
          toast.success(`Vote recorded! You earned ${result.pointsAwarded} points.`, {
            description: `Points added to your ${poll.country_code || 'account'} account`,
            duration: 5000
          });
        } else {
          toast.success('Your vote has been recorded!');
        }

        // If this is an anonymous vote, trigger any external anonymous voting state updates
        if (!user && currentAnonymousId) {
          debugVoteFlow('CENTRALIZED_ANONYMOUS_VOTE_SUCCESS', { 
            pollId: poll.id, 
            optionId, 
            anonymousId: currentAnonymousId 
          });
          
          window.dispatchEvent(new CustomEvent('anonymousVoteSubmitted', {
            detail: { pollId: poll.id, optionId, anonymousId: currentAnonymousId }
          }));
        }
      } else {
        // Enhanced error messages
        let errorMessage = result.error || 'Failed to submit vote';
        
        if (errorMessage.includes('already voted')) {
          errorMessage = 'You have already voted on this poll.';
        } else if (errorMessage.includes('not active')) {
          errorMessage = 'This poll is no longer accepting votes.';
        } else if (errorMessage.includes('closed')) {
          errorMessage = 'Voting for this poll has ended.';
        } else if (errorMessage.includes('Anonymous voting not allowed')) {
          errorMessage = 'Please sign in to vote on this poll.';
        } else if (errorMessage.includes('Public voting not allowed')) {
          errorMessage = 'Public voting is not enabled for this poll.';
        }
        
        toast.error(errorMessage);
      }

      return result;
    } catch (error) {
      debugVoteFlow('CENTRALIZED_VOTE_ERROR', { error });
      const errorResult = { success: false, error: 'An unexpected error occurred during voting. Please try again.' };
      toast.error(errorResult.error);
      return errorResult;
    } finally {
      setIsProcessing(false);
      activeSubmissions.current.delete(submissionKey);
    }
  }, [user?.id, profile?.role, getAnonymousId]);

  const validateVote = useCallback(async (pollId: string): Promise<boolean> => {
    try {
      const currentAnonymousId = await getAnonymousId();
      const validation = await VotingService.validateVote(pollId, user?.id, currentAnonymousId || undefined);
      return validation.canVote;
    } catch (error) {
      debugVoteFlow('CENTRALIZED_VALIDATION_ERROR', { error });
      return false;
    }
  }, [user?.id, getAnonymousId]);

  const getUserVote = useCallback(async (pollId: string): Promise<number | null> => {
    try {
      const currentAnonymousId = await getAnonymousId();
      return await VotingService.getUserVote(pollId, user?.id, currentAnonymousId || undefined);
    } catch (error) {
      debugVoteFlow('CENTRALIZED_GET_VOTE_ERROR', { error });
      return null;
    }
  }, [user?.id, getAnonymousId]);

  // New method specifically for UI state - gets the selected option for display
  const getUserSelectedOption = useCallback(async (pollId: string): Promise<number | null> => {
    try {
      const currentAnonymousId = await getAnonymousId();
      
      // First check validation to get selected option if already voted
      const validation = await VotingService.validateVote(pollId, user?.id, currentAnonymousId || undefined);
      if (validation.selectedOptionId !== undefined) {
        return validation.selectedOptionId;
      }
      
      // Fallback to getUserVote
      return await VotingService.getUserVote(pollId, user?.id, currentAnonymousId || undefined);
    } catch (error) {
      debugVoteFlow('CENTRALIZED_GET_SELECTED_OPTION_ERROR', { error });
      return null;
    }
  }, [user?.id, getAnonymousId]);

  return {
    submitVote,
    validateVote,
    getUserVote,
    getUserSelectedOption,
    isProcessing
  };
};
